import { useEffect, useRef, useReducer, useCallback } from 'react';
import { SyncEvent, MarketingEmailSendStatusEvent } from '../api/generated/model';
import { circuitBreaker, SSEConnectionConfig } from '../api/circuitBreaker';
import { tokenManager } from '../utils/tokenManager';
import { getAuthMe } from '../api/generated/arrayscapeGamingDashboardAPI';

// Debug flag for enabling verbose logging
// Usage: window.notificationStreamDebug.enabled = true
export const notificationStreamDebug = {
  enabled: false
};

// Expose to browser console for debugging
if (typeof window !== 'undefined') {
  (window as any).notificationStreamDebug = notificationStreamDebug;
}

interface UseNotificationStreamOptions {
  onSyncEvent?: (event: SyncEvent) => void;
  onMarketingEmailSendEvent?: (event: MarketingEmailSendStatusEvent) => void;
  onError?: (error: Error) => void;
  enabled?: boolean;
}

export type ConnectionStatus = 'init' | 'connected' | 'disconnected';

interface UseNotificationStreamReturn {
  isConnected: boolean;
  error: Error | null;
  connectionStatus: ConnectionStatus;
  reconnect: () => void;
}

// State managed by reducer
interface StreamState {
  status: ConnectionStatus;
  error: Error | null;
}

// Actions for state transitions
type StreamAction =
  | { type: 'CONNECTED' }
  | { type: 'DISCONNECTED'; error?: Error }
  | { type: 'RESET' };

// Reducer for atomic state transitions
function streamReducer(state: StreamState, action: StreamAction): StreamState {
  switch (action.type) {
    case 'CONNECTED':
      return {
        status: 'connected',
        error: null,
      };

    case 'DISCONNECTED':
      return {
        status: 'disconnected',
        error: action.error || null,
      };

    case 'RESET':
      return {
        status: 'init',
        error: null,
      };

    default:
      return state;
  }
}

/**
 * Custom hook to manage SSE connection for real-time sync events.
 * Notifications are fetched via REST API polling, not SSE.
 */
export function useNotificationStream(
  options: UseNotificationStreamOptions = {}
): UseNotificationStreamReturn {
  const { onSyncEvent, onMarketingEmailSendEvent, onError, enabled = true } = options;

  // Use reducer for atomic state management
  const [state, dispatch] = useReducer(streamReducer, {
    status: 'init',
    error: null,
  });

  const isReconnectQueuedRef = useRef<boolean>(false);

  const connect = useCallback(async () => {
    if (!enabled) return;
    if (isReconnectQueuedRef.current) return; // Already queued

    if (notificationStreamDebug.enabled) {
      console.log('[useNotificationStream] Queueing SSE connection with circuit breaker');
    }
    isReconnectQueuedRef.current = true;

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
    const url = `${apiBaseUrl}/api/v1/notifications/stream`;

    const config: SSEConnectionConfig = {
      url,

      onOpen: () => {
        if (notificationStreamDebug.enabled) {
          console.log('[useNotificationStream] SSE connection opened');
        }
        dispatch({ type: 'CONNECTED' });
        isReconnectQueuedRef.current = false;
      },

      onEvent: (eventType: string, data: string) => {
        try {
          if (['sync-started', 'sync-progress', 'sync-completed', 'sync-failed'].includes(eventType)) {
            const syncEvent: SyncEvent = JSON.parse(data);
            if (onSyncEvent) {
              onSyncEvent(syncEvent);
            }
          } else if (['marketing-email-send-started', 'marketing-email-send-progress', 'marketing-email-send-accepted', 'marketing-email-send-failed'].includes(eventType)) {
            const sendEvent: MarketingEmailSendStatusEvent = JSON.parse(data);
            if (onMarketingEmailSendEvent) {
              onMarketingEmailSendEvent(sendEvent);
            }
          } else if (eventType === 'auth-error') {
            if (notificationStreamDebug.enabled) {
              console.log('[useNotificationStream] Auth error received - token expired/invalid');
            }
          } else if (eventType === 'token-refresh') {
            if (notificationStreamDebug.enabled) {
              console.log('[useNotificationStream] Token refresh requested by server');
            }
            circuitBreaker.queueTokenRefresh().catch((err) => {
              console.error('[useNotificationStream] Failed to queue token refresh:', err);
            });
          }
          // Note: 'notification' events are ignored - notifications are polled via REST API
        } catch (err) {
          console.error(`[useNotificationStream] Failed to parse event ${eventType}:`, err);
        }
      },

      onError: async (error: Error) => {
        if (notificationStreamDebug.enabled) {
          console.log('[useNotificationStream] SSE error received:', error.message);
        }

        // Update UI state
        dispatch({ type: 'DISCONNECTED', error });

        if (onError) {
          onError(error);
        }

        // Clear queue flag
        isReconnectQueuedRef.current = false;

        // Check if this is an auth error (user deleted, token invalid, etc.)
        // SSE doesn't expose HTTP status, so we verify by checking auth
        if (tokenManager.getToken()) {
          try {
            await getAuthMe();
            // Auth succeeded - this was a network error, retry SSE
            if (enabled) {
              if (notificationStreamDebug.enabled) {
                console.log('[useNotificationStream] Auth OK, re-queueing SSE connection');
              }
              setTimeout(() => {
                connect();
              }, 100);
            }
          } catch (authError: any) {
            // Auth failed - likely user was deleted or token is invalid
            if (authError?.response?.status === 401) {
              if (notificationStreamDebug.enabled) {
                console.log('[useNotificationStream] Auth failed (401), redirecting to login');
              }
              tokenManager.handleAuthError();
              return; // Don't retry SSE
            }
            // Other error (network), retry SSE
            if (enabled) {
              if (notificationStreamDebug.enabled) {
                console.log('[useNotificationStream] Auth check failed (not 401), re-queueing SSE');
              }
              setTimeout(() => {
                connect();
              }, 100);
            }
          }
        }
        // No token - don't retry SSE (shouldn't happen, but be safe)
      },
    };

    try {
      await circuitBreaker.queueSSEConnection(config);
    } catch (err) {
      console.error('[useNotificationStream] Failed to queue SSE connection:', err);
      isReconnectQueuedRef.current = false;
    }
  }, [enabled, onSyncEvent, onMarketingEmailSendEvent, onError]);

  const reconnect = useCallback(() => {
    isReconnectQueuedRef.current = false; // Reset queue flag for manual reconnect
    dispatch({ type: 'RESET' }); // Reset state for manual reconnect
    connect();
  }, [connect]);

  // Connect on mount and when enabled changes
  useEffect(() => {
    if (enabled) {
      connect();
    }

    // Cleanup on unmount - circuit breaker manages EventSource now
    return () => {
      // Nothing to clean up - circuit breaker owns the connection
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]); // Only re-run when enabled changes, not when connect changes

  return {
    isConnected: state.status === 'connected',
    error: state.error,
    connectionStatus: state.status,
    reconnect,
  };
}
